const RETURNING_USER_COOKIE = "pf-returning=1";
const AUTH_WARMUP_URL = "/pf-auth/warmup";
const MAINWEB_WARMUP_URL = "/pf-mainweb/warmup";

// Ceiling on how long the app shell holds its spinner for a cold pf-mainweb.
// A balloon-preempted wake lands ~10-15s, but a wake that has to provision a
// node runs into minutes (k8/values.yaml), and a warmup endpoint that is simply
// unreachable would never settle at all. Past this we let the app render and
// fall back to Drive's skeleton rather than trap the user behind a spinner.
const MAINWEB_WARMUP_TIMEOUT_MS = 30_000;

declare global {
  interface Window {
    // Set by public/warmup.js when the returning-user cookie is present.
    __pfMainwebWarmup?: Promise<unknown>;
  }
}

export const warmAuth = () => {
  void fetch(AUTH_WARMUP_URL, { cache: "no-store" }).catch(() => undefined);
};

const pingMainweb = (): Promise<unknown> => {
  // The shell already has one in flight whenever the returning-user cookie is
  // set; reuse it rather than waking pf-mainweb twice per page load.
  if (window.__pfMainwebWarmup) return window.__pfMainwebWarmup;
  try {
    return fetch(MAINWEB_WARMUP_URL, { cache: "no-store" });
  } catch {
    // No fetch to speak of — settle rather than take the app down with us.
    return Promise.resolve();
  }
};

let mainwebWarmup: Promise<void> | undefined;
let mainwebSettled = false;

/** True once the wake ping has come back (or the wait ceiling expired). */
export const isMainwebWarmupSettled = () => mainwebSettled;

/**
 * Resolve when pf-mainweb is serving — i.e. when the KEDA interceptor releases
 * the wake ping — or when MAINWEB_WARMUP_TIMEOUT_MS expires, whichever is first.
 *
 * Never rejects: a failed ping settles the gate too, so a warmup endpoint that
 * is broken or blocked degrades to the pre-gate behaviour instead of blocking
 * the app. Memoised, so the shell's gate, the login page and any later caller
 * all share one in-flight request per page load.
 */
export const whenMainwebWarm = (): Promise<void> => {
  mainwebWarmup ??= Promise.race([
    pingMainweb(),
    new Promise(resolve => setTimeout(resolve, MAINWEB_WARMUP_TIMEOUT_MS)),
  ])
    .catch(() => undefined)
    .then(() => {
      mainwebSettled = true;
    });
  return mainwebWarmup;
};

export const warmMainweb = () => {
  void whenMainwebWarm();
};

export const warmMainwebForReturningUser = () => {
  const isReturningUser = document.cookie.split("; ").includes(RETURNING_USER_COOKIE);
  if (isReturningUser) {
    warmMainweb();
  }
};

/** Test-only: drop the memoised ping so each test starts from a cold shell. */
export const resetMainwebWarmupForTest = () => {
  mainwebWarmup = undefined;
  mainwebSettled = false;
};
